import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import Select, {
  ActionMeta,
  MultiValue,
  OptionProps,
  components,
  createFilter,
} from 'react-select';
import { useDispatch, useSelector } from 'react-redux';
import tabId from 'services/tabId';
import { ApplicationState, persistor } from 'store';
import { ProfileState, ProfileTypes } from 'store/modules/profile_store/types';
import { MenusBehaviourData, MenusBehaviourTypes } from 'store/modules/menus_behaviour_store/types';
import Onboarding from 'components/molecules/onboarding';
import { hasCookie } from 'services/actions/cookies_actions';
import colors from 'styles/colors';
import { getUserContacts } from 'store/modules/user_contacts_store/actions';
import companyId from 'services/company_id';
import { maskCnpj } from 'utils/cnpj';
import {
  ALL_COMPANIES,
  CompanyRequirement,
  CompanyToggle,
  FEATURE_REQUIREMENT,
  companyName,
  companyRequirement,
  getSelectedCompanies,
  isAllSelected,
  missingRequiredCompany,
  selectableCompanies,
  selectionLabel,
  toggleCompany,
} from 'services/selected_companies';
import { Backdrop, Container } from './styles';

interface CompanyOption {
  value: CompanyToggle;
  label: string;
  cnpj?: string;
  own?: boolean;
}

interface CompanyMultiSelectProps {
  callbackSelect: () => void;
}

const sameSelection = (a: number[], b: number[]) =>
  a.length === b.length && a.every((id) => b.includes(id));

const CompanyOptionItem = (props: OptionProps<CompanyOption, true>) => {
  const { data, isSelected, isDisabled } = props;

  return (
    <components.Option {...props}>
      <div className="d-flex align-items-start">
        <input
          type="checkbox"
          className="company-checkbox"
          checked={isSelected}
          disabled={isDisabled}
          readOnly
          tabIndex={-1}
        />
        <div className="company-option-text">
          <span className="d-block">{data.label}</span>
          {data.own && <small className="company-option-own d-block">Minha empresa</small>}
          {data.cnpj && <small className="company-option-cnpj">{maskCnpj(data.cnpj)}</small>}
        </div>
      </div>
    </components.Option>
  );
};

const requiredTip = (requirement: CompanyRequirement, feature: string | null) => {
  if (feature) return `Selecione uma empresa para usar o ${feature}.`;
  if (requirement === 'any') return 'Selecione uma ou mais empresas para prosseguir.';

  return 'Selecione apenas uma empresa para prosseguir.';
};

const CompanyMultiSelect = (props: CompanyMultiSelectProps) => {
  const { callbackSelect } = props;
  const { profile } = useSelector<ApplicationState, ProfileState>((state) => state.storage.profile);
  const { chatbotAssistant, feedbackModal } = useSelector<ApplicationState, MenusBehaviourData>(
    (state) => state.session.menus_behaviour.menus_behaviour,
  );
  const dispatch = useDispatch();
  const { pathname } = useLocation();

  const [feature, setFeature] = useState<string | null>(null);
  const requirement = feature ? FEATURE_REQUIREMENT : companyRequirement(pathname);
  const companies = selectableCompanies(profile);
  const allIds = companies.map(({ id }) => id);
  const committed = getSelectedCompanies(profile);
  const single = requirement === 'single';
  const committedSingle = committed.length === 1 ? committed : [];
  const mustChoose = companies.length > 0 && missingRequiredCompany(requirement, committed);
  const [draft, setDraftState] = useState<number[]>(single ? committedSingle : committed);
  const draftRef = useRef(draft);
  const setDraft = (next: number[]) => {
    draftRef.current = next;
    setDraftState(next);
  };
  const menuOpened = useRef(false);
  const committedKey = committed.join(',');

  useEffect(() => {
    if (!menuOpened.current && !mustChoose) setDraft(single ? committedSingle : committed);
  }, [committedKey, single]);

  useEffect(() => {
    tabId();
    callbackSelect();
  }, []);

  useEffect(() => {
    dispatch(getUserContacts(companyId(profile)));
  }, [profile.currentCompany[tabId()]]);

  const featureBlocked = missingRequiredCompany(FEATURE_REQUIREMENT, committed);

  useEffect(() => {
    let opened: string | null = null;
    if (chatbotAssistant) opened = 'Consultor Jurídico';
    if (feedbackModal) opened = 'Envie seu feedback';
    if (!opened || !featureBlocked) return;

    dispatch({
      type: chatbotAssistant
        ? MenusBehaviourTypes.CLOSE_CHATBOT_ASSISTANT
        : MenusBehaviourTypes.CLOSE_FEEDBACK_MODAL,
    });
    setDraft([]);
    setFeature(opened);
  }, [chatbotAssistant, feedbackModal, featureBlocked, dispatch]);

  const companyOptions: CompanyOption[] = companies.map((company) => ({
    value: company.id,
    label: companyName(company),
    cnpj: company.cnpj,
    own: company.id === profile.clientId,
  }));
  const options: CompanyOption[] =
    companyOptions.length > 1
      ? [{ value: ALL_COMPANIES, label: 'Todas as empresas' }, ...companyOptions]
      : companyOptions;

  const value = options.filter(({ value: optionValue }) =>
    optionValue === ALL_COMPANIES ? isAllSelected(draft, allIds) : draft.includes(optionValue),
  );

  const commit = (companyIds: number[]) => {
    if (sameSelection(companyIds, committed)) return;

    dispatch({ type: ProfileTypes.UPDATE_SELECTED_COMPANIES, companyIds, tabId: tabId() });
    persistor.flush().then(() => window.location.reload());
  };

  const handleChange = (_: MultiValue<CompanyOption>, meta: ActionMeta<CompanyOption>) => {
    if (!meta.option) return;
    const clicked = meta.option.value;
    if (!single) {
      setDraft(toggleCompany(draft, clicked, allIds));
      return;
    }
    if (clicked === ALL_COMPANIES) return;
    setDraft([clicked]);
    commit([clicked]);
  };

  const handleMenuOpen = () => {
    menuOpened.current = true;
    setDraft(single ? committedSingle : committed);
  };

  const handleMenuClose = () => {
    const opened = menuOpened.current || mustChoose;
    menuOpened.current = false;
    if (!single && opened) commit(draftRef.current);
    if (feature) {
      setFeature(null);
      setDraft(committed);
    }
  };

  const selectStyles = {
    control: (styles: any) => ({
      ...styles,
      background: colors.$japanese_indigo,
      cursor: 'pointer',
    }),
    placeholder: (styles: any) => ({
      ...styles,
      color: 'white',
    }),
    input: (styles: any) => ({
      ...styles,
      color: 'white',
    }),
    menu: (styles: any) => ({
      ...styles,
      zIndex: 1050,
    }),
    option: (styles: any, state: OptionProps<CompanyOption, true>) => ({
      ...styles,
      color: state.isDisabled ? colors.$gray500 : 'black',
      cursor: state.isDisabled ? 'default' : 'pointer',
      backgroundColor: state.isFocused && !state.isDisabled ? colors.$gray200 : 'white',
    }),
  };

  return (
    <Container className="representant-select order-3 order-md-2 d-flex align-items-center">
      <Select<CompanyOption, true>
        isMulti
        styles={selectStyles}
        className="filter-input flex-grow-1 tour-select-company"
        options={options}
        value={value}
        onChange={handleChange}
        placeholder={selectionLabel(draft, companies)}
        controlShouldRenderValue={false}
        hideSelectedOptions={false}
        closeMenuOnSelect={single}
        blurInputOnSelect={false}
        menuIsOpen={mustChoose || undefined}
        isOptionDisabled={(option) => single && option.value === ALL_COMPANIES}
        backspaceRemovesValue={false}
        isClearable={false}
        components={{ Option: CompanyOptionItem }}
        filterOption={createFilter({
          stringify: (option) => `${option.label} ${option.data.cnpj || ''}`,
        })}
        noOptionsMessage={() => 'Nenhuma empresa encontrada'}
        onMenuOpen={handleMenuOpen}
        onMenuClose={handleMenuClose}
        onFocus={() => callbackSelect()}
      />
      {mustChoose && (
        <>
          <div className="company-required-tip d-none d-md-block" role="status">
            {requirement && requiredTip(requirement, feature)}
          </div>
          {createPortal(<Backdrop aria-hidden="true" />, document.body)}
        </>
      )}
      {!hasCookie('onboardingRepresented') && <Onboarding tourSelect="onboardingRepresented" />}
    </Container>
  );
};

export default CompanyMultiSelect;
